import React, {
  createContext,
  useContext,
  useState,
  type ReactNode,
} from 'react';
import CareMapprPrivacyDetails from './CareMapprPrivacyDetails';
import CareMapprTermsDetails from './CareMapprTermsDetails';
import MyPainHealthPrivacy from './mypainhealth/MyPainHealthPrivacy';
import MyPainHealthTerms from './mypainhealth/MyPainHealthTerms';

type LegalContentGroup = 'privacy' | 'terms';

type LegalTenancyKey = 'mypainhealth' | 'default';

type LegalContentRegistry = {
  privacy: Record<LegalTenancyKey, ReactNode>;
  terms: Record<LegalTenancyKey, ReactNode>;
};

type TenancyButtonConfig = {
  key: LegalTenancyKey;
  label: string;
  variant?: 'primary' | 'secondary' | 'link';
};

const defaultRegistry: LegalContentRegistry = {
  privacy: {
    mypainhealth: <MyPainHealthPrivacy />,
    default: <CareMapprPrivacyDetails />,
  },
  terms: {
    mypainhealth: <MyPainHealthTerms />,
    default: <CareMapprTermsDetails />,
  },
};

const defaultTenancyButtons: TenancyButtonConfig[] = [
  {
    key: 'mypainhealth',
    label: 'mypainhealth',
  },
  {
    key: 'default',
    label: 'All other tenancies/organisations',
    variant: 'secondary',
  },
];

type LegalContentContextValue = {
  getContent: (group: LegalContentGroup, key: LegalTenancyKey) => ReactNode;
  selectedTenancyKey: LegalTenancyKey | null;
  setSelectedTenancyKey: (key: LegalTenancyKey | null) => void;
  tenancyButtons: TenancyButtonConfig[];
};

const LegalContentContext = createContext<LegalContentContextValue>({
  getContent: (group, key) => defaultRegistry[group][key],
  selectedTenancyKey: null,
  setSelectedTenancyKey: () => {},
  tenancyButtons: defaultTenancyButtons,
});

export const useLegalContent = () => useContext(LegalContentContext);

type ProviderProps = {
  children: React.ReactNode;
  registry?: Partial<LegalContentRegistry>;
  tenancyButtons?: TenancyButtonConfig[];
};

export const LegalContentProvider = ({
  children,
  registry,
  tenancyButtons,
}: ProviderProps) => {
  const [selectedTenancyKey, setSelectedTenancyKey] =
    useState<LegalTenancyKey | null>(null);

  const merged: LegalContentRegistry = {
    privacy: { ...defaultRegistry.privacy, ...(registry?.privacy || {}) },
    terms: { ...defaultRegistry.terms, ...(registry?.terms || {}) },
  };
  const value: LegalContentContextValue = {
    getContent: (group, key) => merged[group][key],
    selectedTenancyKey,
    setSelectedTenancyKey,
    tenancyButtons: tenancyButtons || defaultTenancyButtons,
  };
  return (
    <LegalContentContext.Provider value={value}>
      {children}
    </LegalContentContext.Provider>
  );
};

export type {
  LegalContentGroup,
  LegalTenancyKey,
  LegalContentRegistry,
  TenancyButtonConfig,
};
